import * as React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { error } from 'react-notification-system-redux';
import cn from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import { Button } from '@topconagriculture/react-components';
import { ModalContent, useModal, useModalTitle } from '@topconagriculture/modal';
import { useDashboardConfig, useDashboardContext, useWidgetsListQuery, useWidgetsMutation } from '../../hooks';
import { useAppPermissions } from '../../hooks/useAppPermissions';
import { widgetsConfigMap } from '../Widget/WidgetConfig';
import { notificationsConfig } from '../../config/constants';
import { useQueryClient } from '@tanstack/react-query';
const useStyles = makeStyles((theme) => ({
    formContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: 920,
        height: '80%',
        overflowY: 'auto',
        '& > div': {
            width: '100%',
        },
    },
    formContainerOne: {
        maxWidth: 460,
        overflowY: 'hidden',
    },
    widgetGridContainer: {
        display: 'inline-block',
        padding: '8px 4px',
    },
    widgetGridContainerDisabled: {
        opacity: 0.7,
    },
    widgetThumbnail: {
        float: 'left',
        width: 225,
        height: 205,
        borderRadius: 5,
        boxShadow: '0 2px 4px 0 rgba(0,0,0,.3)',
    },
    widgetInfoContainer: {
        float: 'right',
        width: 'calc(100% - 225px)',
        height: 'calc(100% - 66px)',
        padding: '0 8px 0 15px',
    },
    widgetTitle: {
        display: 'block',
        textTransform: 'uppercase',
        fontSize: theme.font.size.medium,
        fontWeight: theme.font.weight.bold,
    },
    widgetDescription: {
        display: 'block',
        fontSize: theme.font.size.mediumSmall,
        fontWeight: theme.font.weight.light,
    },
    widgetSpacing: {
        padding: 10,
        margin: 0,
    },
    addUserBtn: {
        height: 'auto',
        width: 'auto',
        maxHeight: 50,
    },
    widgetInfoSize: {
        border: `1px solid ${theme.color.grey.dark}`,
        color: theme.color.grey.dark,
        fontSize: theme.font.size.small,
        padding: 3,
        float: 'right',
    },
    widgetInfoFooter: {
        width: 'calc(100% - 225px)',
        padding: '0 8px 0 15px',
        float: 'right',
    },
    widgetInfoFooterDimension: {
        width: 'calc(100% - 25px)',
        paddingBottom: 10,
        fontSize: theme.font.size.mediumSmall,
    },
}));
const WidgetAddModal = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { closeModal } = useModal();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { data: widgets } = useWidgetsListQuery();
    const appPermissions = useAppPermissions();
    const dashboardContext = useDashboardContext();
    const { RESOURCES_BASE_URL } = useDashboardConfig();
    const { mutate: updateWidgets } = useWidgetsMutation();
    useModalTitle(t('widget:generic:add_new_widget'));
    const viewableWidgets = Object.values(widgetsConfigMap).filter((widgetConfig) => {
        if (!widgetConfig)
            return false;
        const requiredPermissions = widgetConfig.permissions;
        return !requiredPermissions || requiredPermissions.every((appId) => appPermissions[appId]);
    });
    const numberOfWidgets = viewableWidgets.length;
    const onSubmit = useCallback(async (widgetConfig) => {
        if (widgetConfig.init) {
            const uuid = uuidv4();
            try {
                closeModal();
                const config = await widgetConfig.init(dashboardContext);
                updateWidgets([...(widgets || []), { ...config, uuid }]);
                queryClient.setQueryData(['widgets'], (prevData) => {
                    return [...(prevData || []), { ...widgetConfig.baseConfig, uuid }];
                });
            }
            catch (err) {
                dispatch(error({
                    ...notificationsConfig.defaultConf,
                    translationKey: err.message || 'widget:error_generic',
                }));
            }
        }
        else {
            updateWidgets([...(widgets || []), { ...widgetConfig.baseConfig, uuid: uuidv4() }]);
            closeModal();
        }
    }, [updateWidgets, closeModal]);
    return (React.createElement(ModalContent, null,
        React.createElement("form", { className: numberOfWidgets > 1 ? classes.formContainer : classes.formContainerOne },
            React.createElement(Grid, { container: true }, viewableWidgets.map((widgetConfig, index) => {
                const alreadyInDashboard = !!(widgets === null || widgets === void 0 ? void 0 : widgets.find((widget) => widget.type === widgetConfig.type));
                const addDisabled = widgetConfig.single === true && alreadyInDashboard;
                return (React.createElement(Grid, { item: true, sm: numberOfWidgets > 1 ? 6 : 12, key: index, className: cn(classes.widgetGridContainer, addDisabled && classes.widgetGridContainerDisabled) },
                    React.createElement("img", { className: classes.widgetThumbnail, src: `${RESOURCES_BASE_URL}${widgetConfig.thumbnail}`, alt: t(widgetConfig.title) }),
                    React.createElement("div", { className: classes.widgetInfoContainer },
                        React.createElement("span", { className: classes.widgetTitle }, t(widgetConfig.title)),
                        React.createElement("span", { className: classes.widgetDescription }, t(widgetConfig.description))),
                    React.createElement("div", { className: classes.widgetInfoFooter },
                        React.createElement("div", { className: classes.widgetInfoFooterDimension },
                            React.createElement("span", null,
                                t('widget:generic:widget_dimension'),
                                ":"),
                            React.createElement("span", { className: `${classes.widgetInfoSize} ${widgetConfig.baseConfig.w}x${widgetConfig.baseConfig.h}` },
                                widgetConfig.baseConfig.w,
                                "x",
                                widgetConfig.baseConfig.h)),
                        React.createElement(Button, { className: classes.addUserBtn, onClick: () => onSubmit(widgetConfig), label: t('widget:generic:add_widget'), iconName: "add", iconColor: "white", iconSize: 20, disabled: addDisabled }))));
            })))));
};
export default WidgetAddModal;
//# sourceMappingURL=WidgetAddModal.js.map